import { CalendarRangeIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { useFormatters } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';
import { useMediaQuery } from '@/shared/lib/hooks/useMediaQuery';
import { Button } from '@/shared/ui/button';
import { Calendar } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';

import { DATE_RANGE_PRESETS, resolveDateRangePreset } from './presets';

export type { DateRange };

export interface DateRangePickerProps {
  value: DateRange | null;
  onChange: (value: DateRange | null) => void;
  placeholder?: string;
  /** Show quick presets (default true). */
  presets?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

/** Date range picker with presets; two months side by side on wide screens. */
export function DateRangePicker({
  value,
  onChange,
  placeholder,
  presets = true,
  clearable = true,
  disabled,
  invalid,
  id,
  className,
  ...aria
}: DateRangePickerProps) {
  const { t } = useTranslation('shared');
  const fmt = useFormatters();
  const wide = useMediaQuery('(min-width: 640px)');
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date | undefined>(value?.from);

  const label = value?.from
    ? value.to && value.to.getTime() !== value.from.getTime()
      ? `${fmt.date(value.from, 'date')} – ${fmt.date(value.to, 'date')}`
      : fmt.date(value.from, 'date')
    : null;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setMonth(value?.from);
      }}
    >
      <div className={cn('relative inline-flex w-full min-w-0', className)}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-label={aria['aria-label']}
            className={cn(
              'w-full justify-start px-3 font-normal tabular-nums',
              !label && 'text-subtle-foreground',
              clearable && label && 'pr-8',
            )}
          >
            <CalendarRangeIcon className="text-muted-foreground" />
            <span className="truncate">{label ?? placeholder ?? t('date.pickRange')}</span>
          </Button>
        </PopoverTrigger>
        {clearable && label && !disabled && (
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label={t('actions.clear')}
            className="absolute top-1/2 right-2 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <XIcon className="size-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto max-w-[calc(100vw-1rem)] p-0" align="start">
        <div className="flex flex-col sm:flex-row">
          {presets && (
            <div className="flex gap-1 overflow-x-auto border-b p-2 sm:w-40 sm:flex-col sm:border-r sm:border-b-0">
              {DATE_RANGE_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  size="sm"
                  variant="ghost"
                  className="shrink-0 justify-start font-normal"
                  onClick={() => {
                    const range = resolveDateRangePreset(preset, new Date());
                    onChange(range);
                    setMonth(range.from);
                    setOpen(false);
                  }}
                >
                  {t(`date.presets.${preset}`)}
                </Button>
              ))}
            </div>
          )}
          <Calendar
            mode="range"
            selected={value ?? undefined}
            onSelect={(range) => onChange(range ?? null)}
            month={month}
            onMonthChange={setMonth}
            numberOfMonths={wide ? 2 : 1}
            autoFocus
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
